import React, { useState, useEffect, useCallback } from 'react';
import SimpleCryptoLogo from '../../SimpleCryptoLogo';

export default function CurrentHoldingsStep({ onNext, onBack, data }) {
  const [holdings, setHoldings] = useState(data.holdings || [
    { symbol: 'ETH', name: 'Ethereum', quantity: '0' },
    { symbol: 'BTC', name: 'Bitcoin', quantity: '0' },
    { symbol: 'SOL', name: 'Solana', quantity: '0' },
  ]);
  const [cashToInvest, setCashToInvest] = useState(data.cashToInvest || '5000');
  const [currency, setCurrency] = useState('USD');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  
  // Close currency dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCurrencyDropdown && !event.target.closest('.currency-dropdown')) {
        setShowCurrencyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCurrencyDropdown]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Popular cryptocurrencies for quick selection (will be loaded from API)
  const [popularCoins, setPopularCoins] = useState([
    { symbol: 'BTC', name: 'Bitcoin', quantity: '0' },
    { symbol: 'ETH', name: 'Ethereum', quantity: '0' },
    { symbol: 'SOL', name: 'Solana', quantity: '0' },
    { symbol: 'ADA', name: 'Cardano', quantity: '0' },
    { symbol: 'DOT', name: 'Polkadot', quantity: '0' },
    { symbol: 'LINK', name: 'Chainlink', quantity: '0' },
    { symbol: 'UNI', name: 'Uniswap', quantity: '0' },
    { symbol: 'AVAX', name: 'Avalanche', quantity: '0' }
  ]);

  const updateQuantity = (symbol, quantity) => {
    setHoldings(holdings.map(holding => 
      holding.symbol === symbol ? { ...holding, quantity } : holding
    ));
  };

  const addCoin = (coin) => {
    // Ensure symbol is UPPERCASE and prevent duplicates
    const normalizedSymbol = coin.symbol.toUpperCase();
    if (!holdings.find(h => h.symbol === normalizedSymbol)) {
      setHoldings([...holdings, { 
        ...coin, 
        symbol: normalizedSymbol, 
        quantity: '0' 
      }]);
    }
    setSearchTerm('');
    setShowSearch(false);
  };

  const removeCoin = (symbol) => {
    setHoldings(holdings.filter(h => h.symbol !== symbol));
  };

  const handleNext = () => {
    const validHoldings = holdings.filter(h => parseFloat(h.quantity) > 0);
    const validCashToInvest = parseFloat(cashToInvest) || 0;
    
    // Ensure user has at least some holdings or cash to invest
    if (validHoldings.length === 0 && validCashToInvest === 0) {
      // You could add a toast notification here if you want to show an error
      return; // Don't proceed if no valid data
    }
    
    onNext({ 
      holdings: validHoldings,
      cashToInvest: validCashToInvest,
      currency: currency
    });
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (query) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (query && query.trim().length > 0) {
            performSearch(query);
          } else {
            setSearchResults([]);
          }
        }, 250);
      };
    })(),
    []
  );

  // Perform search using the new crypto search API
  const performSearch = async (query) => {
    try {
      setIsSearching(true);
      const response = await fetch(`/api/crypto/search?limit=500&query=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        // Transform API results to match our component's expected format
        const transformedResults = data.options.map(coin => ({
          symbol: coin.symbol,
          name: coin.name,
          quantity: '0'
        }));
        setSearchResults(transformedResults);
      } else {
        console.error('Search failed:', response.status);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };



  // Load popular coins from API on component mount
  useEffect(() => {
    const loadPopularCoins = async () => {
      try {
        const response = await fetch('/api/crypto/search?limit=8');
        if (response.ok) {
          const data = await response.json();
          const transformedResults = data.options.map(coin => ({
            symbol: coin.symbol,
            name: coin.name,
            quantity: '0'
          }));
          setPopularCoins(transformedResults);
        }
      } catch (error) {
        console.error('Failed to load popular coins:', error);
        // Keep default popular coins if API fails
      }
    };
    loadPopularCoins();
  }, []);

  // Update search when searchTerm changes
  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cdefs%3E%3Cpattern id='grid' width='10' height='10' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 10 0 L 0 0 0 10' fill='none' stroke='rgba(255,255,255,0.1)' stroke-width='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23grid)'/%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="flex-1 flex flex-col justify-start items-center px-4 pt-4 relative z-10">
        {/* Logo and Brand */}
        <div className="text-center mb-6 px-2 mt-2">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg">
            <span className="text-white font-bold text-lg">CI</span>
          </div>
          <h1 className="text-white text-lg font-semibold">CryptoIndicators.AI</h1>
        </div>

        {/* Main Question */}
        <div className="text-center mb-6 max-w-md px-2">
          <h2 className="text-2xl font-bold text-white mb-2">
            What are you holding right now?
          </h2>
        </div>

        {/* Search Bar */}
        <div className="w-full max-w-md mb-6 px-2">
                          <div className="relative currency-dropdown">
            <input
              type="text"
              placeholder="Search for coin"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setShowSearch(true)}
              className="w-full px-6 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {showSearch && searchTerm && (
              <div className="absolute top-full left-0 right-0 bg-gray-800 border border-gray-700 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto mt-2">
                {isSearching ? (
                  <div className="px-4 py-3 text-center text-gray-400">
                    Searching...
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map(coin => (
                    <button
                      key={coin.symbol}
                      onClick={() => addCoin(coin)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-700 flex items-center space-x-3 border-b border-gray-700 last:border-b-0"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        {/* DEBUG: Holdings search result logo */}
                        {console.log('🔍 HOLDINGS SEARCH - Symbol:', coin.symbol, 'Final:', coin.symbol + 'USD')}
                        <SimpleCryptoLogo symbol={coin.symbol + 'USD'} width={24} height={24} />
                      </div>
                                              <div>
                          <div className="font-medium text-white">{coin.symbol}</div>
                          <div className="text-gray-400 text-sm">{coin.name}</div>
                        </div>
                    </button>
                  ))
                ) : searchTerm.trim().length > 0 ? (
                  <div className="px-4 py-3 text-center text-gray-400">
                    No results found
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Holdings Cards */}
        <div className="w-full max-w-md space-y-2 mb-6 px-2">
          {holdings.map((holding) => (
            <div key={holding.symbol} className="bg-gray-800/50 border border-gray-700 rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  {/* DEBUG: Holdings card logo */}
                  {console.log('🔍 HOLDINGS CARD - Symbol:', holding.symbol, 'Final:', holding.symbol + 'USD')}
                  <SimpleCryptoLogo symbol={holding.symbol + 'USD'} width={32} height={32} />
                </div>
                <div>
                  <div className="font-semibold text-white text-lg">{holding.symbol}</div>
                  {holding.symbol === 'Cash to invest' && (
                    <div className="text-gray-400 text-sm">Cash to invest</div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={holding.quantity}
                    onChange={(e) => updateQuantity(holding.symbol, e.target.value)}
                    className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="0"
                  />
                  <button className="text-gray-400 hover:text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                  </button>
                </div>
                
                {holding.symbol === 'Cash to invest' && (
                  <div className="relative currency-dropdown">
                    <button 
                      onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg flex items-center space-x-1 hover:bg-blue-700 transition-colors"
                    >
                      <span>{currency}</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {showCurrencyDropdown && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[80px]">
                        <button
                          onClick={() => {
                            setCurrency('USD');
                            setShowCurrencyDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                            currency === 'USD' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                          }`}
                        >
                          USD
                        </button>
                        <button
                          onClick={() => {
                            setCurrency('CAD');
                            setShowCurrencyDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                            currency === 'CAD' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                          }`}
                        >
                          CAD
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {/* Add Cash to Invest Card */}
          {!holdings.find(h => h.symbol === 'Cash to invest') && (
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white">
                  <span className="text-white font-bold text-sm">$</span>
                </div>
                <div>
                  <div className="font-semibold text-white text-lg">Cash to invest</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={cashToInvest}
                    onChange={(e) => setCashToInvest(e.target.value)}
                    className="w-20 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="0"
                  />
                  <button className="text-gray-400 hover:text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                  </button>
                </div>
                
                <div className="relative currency-dropdown">
                  <button 
                    onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg flex items-center space-x-1 hover:bg-blue-700 transition-colors"
                  >
                    <span>{currency}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showCurrencyDropdown && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[80px]">
                      <button
                        onClick={() => {
                          setCurrency('USD');
                          setShowCurrencyDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                          currency === 'USD' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                        }`}
                      >
                        USD
                      </button>
                      <button
                        onClick={() => {
                          setCurrency('CAD');
                          setShowCurrencyDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                          currency === 'CAD' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                        }`}
                      >
                        CAD
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>



        {/* Next Button */}
        <div className="w-full max-w-md px-2">
          <button
            onClick={handleNext}
            className="w-full py-4 bg-gradient-to-b from-blue-500 to-blue-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
} 